import { faRectangleList, faUser } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { differenceInMilliseconds } from "date-fns";
import { useTranslation } from "react-i18next";
import { Checkbox, Grid, GridColumn, Icon, Label } from "semantic-ui-react";

import { DATE_FORMATS_FNS, FileDropdown, PinTypeIcon, PinUserLabel } from "components";
import "components/PinLog/PinLog.scss";
import { PIN_STATUSES } from "models";
import { getShopIconColor } from "modules/AppointmentDetails/components/Interventions/components";
import { useUpdatePinVisibilityMutation } from "modules/AppointmentDetails/hooks";
import { getNameFromURL } from "utils/common";
import { formatDate } from "utils/dateHelperFunctions";

import type { Appointment, AppointmentNoteAttachment, Intervention, Pin, PinNoteAttachment, SnoozeNoteAttachment } from "models";
import type { DropdownItemProps } from "semantic-ui-react";

type PinLogProps = {
  pinTypeOptions: DropdownItemProps[];
  pinOrderOptions: DropdownItemProps[];
  pinStatusOptions: DropdownItemProps[];
  pinWarrantyOptions: DropdownItemProps[];
  pinLog?: Pin[];
  readOnly?: boolean;
  showTitle?: boolean;
  appointment?: Appointment;
  intervention?: Intervention;
  attachments?: AppointmentNoteAttachment[] | SnoozeNoteAttachment[] | PinNoteAttachment[];
};

export const PinLog = ({
  attachments,
  pinTypeOptions,
  pinWarrantyOptions,
  pinStatusOptions,
  pinOrderOptions,
  pinLog,
  showTitle = true,
  intervention,
  readOnly = false
}: PinLogProps) => {
  const { t } = useTranslation();

  const { mutate: updatePinVisibility } = useUpdatePinVisibilityMutation();

  if (!pinLog?.length) return;

  const sortedPinLog = pinLog.sort((a, b) => differenceInMilliseconds(new Date(b.created_on), new Date(a.created_on)));

  const onImportantClick = (currentPin: Pin) => {
    if (!intervention) return;
    const payload = {
      pin_id: currentPin.id,
      intervention_id: intervention.id,
      visible_important_items: !currentPin.visible_important_items
    };
    updatePinVisibility(payload);
  };

  return (
    <div className="PinLog">
      {showTitle && (
        <Grid className="PinLogHistoryHeading">
          <GridColumn width={14}>
            <p>{t("v8_log_history", "Log History")}</p>
            <FileDropdown files={attachments} iconColor="grey" />
          </GridColumn>
        </Grid>
      )}
      <div className="PinLogHistory">
        {sortedPinLog.map((currentPin, i) => {
          const previousPin = sortedPinLog.at(i + 1);
          const pinType = currentPin.pin_type_id !== previousPin?.pin_type_id && pinTypeOptions.find(item => item.value === currentPin.pin_type_id)?.text;
          const pinWarrantyType =
            currentPin.warranty_type_id !== previousPin?.warranty_type_id && pinWarrantyOptions.find(item => item.value === currentPin.warranty_type_id)?.text;
          const pinStatusType = currentPin.pin_status_id !== previousPin?.pin_status_id && pinStatusOptions.find(item => item.value === currentPin.pin_status_id)?.text;
          const pinOrderType = currentPin.order_status !== previousPin?.order_status && pinOrderOptions.find(item => item.value === currentPin.order_status)?.text;

          const currentPinAttachments = [...(currentPin.note_attachments || [])].map(attachment => ({ url: attachment, name: getNameFromURL(attachment) }));

          return (
            <Grid key={currentPin.id} className="pin-log-item">
              <GridColumn width={16}>
                <div className="pin-info">
                  <PinUserLabel currentPin={currentPin} />

                  <Label basic>{formatDate(currentPin.updated_on, DATE_FORMATS_FNS.dateMonthYearTime)}</Label>

                  <FileDropdown files={currentPinAttachments} iconColor="grey" />

                  <>
                    {currentPin.manufacturer_id && currentPin.manufacturer_id > 0 && currentPin.manufacturer_id !== previousPin?.manufacturer_id && (
                      <Label basic className="log-label">
                        <FontAwesomeIcon icon={faUser} className="i-grey mr-5" />
                        <strong>
                          {t("v8_granted_access_to_the_manufacturer", "granted access to the manufacturer")} {currentPin.manufacturer?.first_name}{" "}
                          {currentPin.manufacturer?.last_name}
                        </strong>
                      </Label>
                    )}
                    {currentPin.pin_status_id === PIN_STATUSES.Deleted && <>{<strong style={{ color: "red" }}>{t("v8_deleted", "Deleted")}</strong>}</>}
                    {currentPin.mechanic_fixed && currentPin.mechanic_fixed !== previousPin?.mechanic_fixed && (
                      <Label basic className="log-label">
                        <FontAwesomeIcon icon={faUser} className="i-grey mr-5" />
                        <strong style={{ color: "blue" }}>{t("v8_marked_as_fixed", "Marked as fixed")}</strong>
                      </Label>
                    )}
                    {currentPin.info && currentPin.info !== previousPin?.info && (
                      <Label basic className="log-label">
                        <FontAwesomeIcon icon={faUser} className="i-grey mr-5" />
                        <strong style={{ color: "blue" }}>{t("v8_marked_as_info", "Marked as info")}</strong>
                      </Label>
                    )}
                  </>

                  {(pinWarrantyType || pinType) && (
                    <Label basic className="log-label">
                      <span className="i-grey mr-5">
                        <PinTypeIcon pinTypeId={currentPin.pin_type_id} isPrint />
                      </span>
                      {pinWarrantyType || pinType}
                    </Label>
                  )}

                  {pinOrderType && (
                    <Label basic className="log-label">
                      <Icon className="shopping cart mr-5" color={getShopIconColor(currentPin.order_status)} />
                      {pinOrderType}
                    </Label>
                  )}
                  {pinStatusType && (
                    <Label basic className="log-label">
                      <FontAwesomeIcon icon={faRectangleList} className="i-grey mr-5" />
                      {pinStatusType}
                    </Label>
                  )}
                  {currentPin.claim_nr && currentPin.claim_nr !== previousPin?.claim_nr && (
                    <Label basic className="log-label">
                      # {t("v8_claim", "Claim")}: {currentPin.claim_nr}
                    </Label>
                  )}
                  {currentPin.support_nr && currentPin.support_nr !== previousPin?.support_nr && (
                    <Label basic className="log-label">
                      # {t("v8_support", "Support")}: {currentPin.support_nr}
                    </Label>
                  )}
                  {currentPin.ref_nr && currentPin.ref_nr !== previousPin?.ref_nr && (
                    <Label basic className="log-label">
                      # {t("v8_reference", "Reference")}: {currentPin.ref_nr}
                    </Label>
                  )}

                  {(currentPin.keep_parts || currentPin.pin_status_id === PIN_STATUSES.Deleted) && (
                    <p className="logHistoryParagraph">
                      {currentPin.pin_status_id === PIN_STATUSES.Deleted ? (
                        t("v8_deleted", "Deleted")
                      ) : (
                        <>
                          <Label basic className="label-container">
                            <Icon className="gear i-green mr-5" />
                            {t("v8_keep_parts", "Keep Parts")}
                          </Label>
                        </>
                      )}
                    </p>
                  )}

                  {(!!currentPin.note || (currentPin.keep_parts && currentPin.visible_important_items)) && (
                    <Label basic className="important-container">
                      <Icon className="exclamation circle mr-5" color="red" />
                      <div>{t("v8_important", "Important")}</div>
                      <Checkbox checked={currentPin.visible_important_items} onChange={() => onImportantClick(currentPin)} disabled={readOnly} />
                    </Label>
                  )}
                </div>
              </GridColumn>
              {currentPin.note && <span className="pin-note">{currentPin.note}</span>}
            </Grid>
          );
        })}
      </div>
    </div>
  );
};
